//  RA-2160 caveat: the IP recorded with this login comes from a third-party lookup (ipify) made in
//  the browser and sent as X-Client-IP. It is client-supplied, so it is for audit only and must
//  never drive access control. It is accurate only while the customer's proxy (e.g. Zscaler)
//  leaves that lookup unproxied; if that policy changes, it silently becomes the proxy IP.
import { baseApiUrl } from 'src/config';
import { tokenType } from '../tokenManagementService/constant';
import { getClientIpHeader } from './getClientIp';

export const LOGIN_CONTEXT_PATH = '/provider/session-activity/login';
export const LOGIN_CONTEXT_GUARD_PREFIX = 'ra2160.loginContext.';

/** The slice of the Keycloak adapter this needs; a parameter so this module never imports it. */
export interface LoginContextAuth {
  authenticated?: boolean;
  token?: string;
  tokenParsed?: { [claim: string]: any };
}

const readGuard = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
};

const writeGuard = (key: string) => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    //  Storage blocked: the server is idempotent per sid, so a repeat call is harmless.
  }
};

const tenantId = () =>
  window.location.hostname === 'localhost'
    ? localStorage.getItem('realm') || ''
    : window.location.hostname.split('-')[0];

/**
 * Tells provider-service a browser login happened, once per Keycloak session (keyed by the token's
 * `sid`). The server reads the sid from the token. This uses plain fetch and not the axios
 * instance, whose 401 handler would log the user out over an audit call.
 *
 * It adds X-Client-IP itself rather than relying on the global fetch wrapper: AuthContext calls it
 * while its module is still loading, and main.tsx only installs the wrapper (installClientIpFetch)
 * once every import has finished, so the wrapper is never in place for this call.
 *
 * Fire and forget: callers must not await it, and it never rejects. Sign-in and first render must
 * not depend on it or on ipify (getClientIpHeader's lookup, which has its own 2s timeout).
 */
export const recordLoginContext = (auth: LoginContextAuth): Promise<void> => {
  try {
    const sid = auth.tokenParsed?.sid;
    if (!auth.authenticated || !auth.token || typeof sid !== 'string' || !sid) {
      return Promise.resolve();
    }
    const guardKey = `${LOGIN_CONTEXT_GUARD_PREFIX}${sid}`;
    if (readGuard(guardKey)) return Promise.resolve();

    return getClientIpHeader()
      .then((ipHeader) =>
        fetch(`${baseApiUrl}${LOGIN_CONTEXT_PATH}`, {
          method: 'POST',
          headers: {
            Authorization: `${tokenType} ${auth.token}`,
            'X-TENANT-ID': tenantId(),
            Facility_id: localStorage.getItem('facilityId') || '',
            ...ipHeader,
          },
        })
      )
      .then((response) => {
        //  Only a 2xx marks the session done, so a failed attempt retries on the next app load.
        if (response.ok) writeGuard(guardKey);
      })
      .catch(() => {
        console.debug('recordLoginContext: not recorded');
      });
  } catch {
    return Promise.resolve();
  }
};
